import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
} from '@angular/fire/firestore';

export interface TimeEntry {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  clockIn: Timestamp;
  clockOut: Timestamp | null;
  hoursWorked: number | null;
  note: string;
}

@Injectable({
  providedIn: 'root',
})
export class TimesheetService {
  private readonly firestore = inject(Firestore);

  calculateDayTotal(entries: TimeEntry[]): number {
    let total = 0;

    for (const entry of entries) {
      if (entry.hoursWorked !== null) {
        total += entry.hoursWorked;
      }
    }

    for (let i = 0; i < entries.length - 1; i++) {
      const current = entries[i];
      const next = entries[i + 1];

      if (!current.clockOut) {
        continue;
      }

      const clockOutDate = current.clockOut.toDate();
      const nextClockInDate = next.clockIn.toDate();
      const gapMinutes =
        (nextClockInDate.getTime() - clockOutDate.getTime()) / (1000 * 60);

      if (gapMinutes > 0 && gapMinutes <= 15) {
        total += gapMinutes / 60;
      }
    }

    return Math.round(total * 100) / 100;
  }

  async clockIn(employeeId: string, employeeName: string): Promise<string> {
    const now = new Date();
    const entriesRef = collection(this.firestore, 'timeEntries');

    const docRef = await addDoc(entriesRef, {
      employeeId,
      employeeName,
      date: this.toDateKey(now),
      clockIn: Timestamp.fromDate(now),
      clockOut: null,
      hoursWorked: null,
      note: '',
    });

    return docRef.id;
  }

  async clockOut(entryId: string): Promise<void> {
    const entryRef = doc(this.firestore, 'timeEntries', entryId);
    const entrySnap = await getDoc(entryRef);

    if (!entrySnap.exists()) {
      throw new Error('Active time entry not found.');
    }

    const data = entrySnap.data() as Partial<TimeEntry>;
    if (!data.clockIn) {
      throw new Error('Time entry is missing clock-in timestamp.');
    }

    const clockOutTime = new Date();
    const clockInTime = data.clockIn.toDate();

    if (clockOutTime <= clockInTime) {
      throw new Error('Clock-out time must be after clock-in time.');
    }

    const hoursWorked = this.calculateHours(clockInTime, clockOutTime);

    await updateDoc(entryRef, {
      clockOut: Timestamp.fromDate(clockOutTime),
      hoursWorked,
    });
  }

  async getActiveEntry(employeeId: string): Promise<TimeEntry | null> {
    const entriesRef = collection(this.firestore, 'timeEntries');
    const q = query(
      entriesRef,
      where('employeeId', '==', employeeId),
      where('clockOut', '==', null),
      orderBy('clockIn', 'desc'),
      limit(1),
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const d = snapshot.docs[0];
    return { id: d.id, ...(d.data() as Omit<TimeEntry, 'id'>) };
  }

  async getEntriesForWeek(
    employeeId: string,
    weekStart: Date,
  ): Promise<TimeEntry[]> {
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const entriesRef = collection(this.firestore, 'timeEntries');
    const q = query(
      entriesRef,
      where('employeeId', '==', employeeId),
      where('date', '>=', this.toDateKey(start)),
      where('date', '<=', this.toDateKey(end)),
      orderBy('date', 'asc'),
      orderBy('clockIn', 'asc'),
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<TimeEntry, 'id'>),
    }));
  }

  async getEntriesForPeriod(
    employeeId: string,
    periodStart: Date,
    periodEnd: Date,
  ): Promise<TimeEntry[]> {
    const start = new Date(periodStart);
    start.setHours(0, 0, 0, 0);

    const end = new Date(periodEnd);
    end.setHours(23, 59, 59, 999);

    const entriesRef = collection(this.firestore, 'timeEntries');
    const q = query(
      entriesRef,
      where('employeeId', '==', employeeId),
      where('date', '>=', this.toDateKey(start)),
      where('date', '<=', this.toDateKey(end)),
      orderBy('date', 'asc'),
      orderBy('clockIn', 'asc'),
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<TimeEntry, 'id'>),
    }));
  }

  async addManualEntry(
    employeeId: string,
    employeeName: string,
    date: string,
    clockIn: Date,
    clockOut: Date,
    note: string = '',
  ): Promise<string> {
    if (clockOut <= clockIn) {
      throw new Error('Time out must be after time in.');
    }

    const entriesRef = collection(this.firestore, 'timeEntries');
    const docRef = await addDoc(entriesRef, {
      employeeId,
      employeeName,
      date,
      clockIn: Timestamp.fromDate(clockIn),
      clockOut: Timestamp.fromDate(clockOut),
      hoursWorked: this.calculateHours(clockIn, clockOut),
      note: note.trim(),
    });

    return docRef.id;
  }

  async updateEntry(
    entryId: string,
    clockIn: Date,
    clockOut: Date,
    note: string,
  ): Promise<void> {
    if (clockOut <= clockIn) {
      throw new Error('Time Out must be after Time In.');
    }

    const entryRef = doc(this.firestore, 'timeEntries', entryId);
    await updateDoc(entryRef, {
      date: this.toDateKey(clockIn),
      clockIn: Timestamp.fromDate(clockIn),
      clockOut: Timestamp.fromDate(clockOut),
      hoursWorked: this.calculateHours(clockIn, clockOut),
      note: note.trim(),
    });
  }

  async deleteEntry(entryId: string): Promise<void> {
    const entryRef = doc(this.firestore, 'timeEntries', entryId);
    await deleteDoc(entryRef);
  }

  private calculateHours(clockIn: Date, clockOut: Date): number {
    const milliseconds = clockOut.getTime() - clockIn.getTime();
    const hours = milliseconds / (1000 * 60 * 60);
    return Math.round(hours * 100) / 100;
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
